import classnames from 'classnames';
import * as R from 'ramda';
import React from 'react';
import styles from 'iss/styles/Table.styl';

/** Displays a bootstrap table with the given data
 * See https://getbootstrap.com/docs/3.3/css/#tables
 *
 * Props:
 * data: An array of objects
 * id: The id to put into the table element
 * striped: Boolean indicating whether to add stripes to the table
 * rowId: An accessor (see below) to give each row a key.
 *        Needs to return a key that is unique to each row.
 *        Defaults to "id" prop of the object
 * emptyTableText: A string that is showed when data is empty
 * columns: An array of column definitions (see below)
 * rowProps: An object containing props to give to each tr element in the table
 *           OR
 *           A function that takes the row data as parameter and returns the props
 *
 * Column definition is an object describing how a column is rendered.
 * {
 *   headerProps: Additional props to give to the column header element
 *                Can also be a function taking the column definition as a parameter
 *   cellProps:   Like headerProps but for normal cell elements.
 *                The function version takes two parameters:
 *                the current data item and the current column definition
 *   header:      The title of the column. Can be a string or React elements
 *   accessor:    An accessor (see below) used to render the cells for each row
 * }
 *
 * Accessors:
 * There are three types of accessors.
 * 1) Functions that take the data item and return it's representation
 * 2) A string representing a prop in an object.
 *    "id" is equivalent to (obj) => obj.id
 * 2) An array representing a path into the object.
 *    ["hair", "length"] is equivalent to (human) => human.hair.length
 *
 */
export function Table({data, columns, rowProps = {}, striped = true, id, rowId = "id", emptyTableText = null}) {
  const getKey = toAccessor(rowId);

  return data.length ? <BootstrapTable id={id} striped={striped}>
    <thead>
      <tr>
        {columns.map((column, i) => <ColumnHeader column={column} key={i} />)}
      </tr>
    </thead>
    <tbody>
      {data.map(item =>
        <Row key={getKey(item)} item={item} columns={columns} rowProps={rowProps} />
      )}
    </tbody>
  </BootstrapTable> : <EmptyTable text={emptyTableText} />;
}

export function BootstrapTable({striped, ...props}) {
  return <table className={classnames("table", {"table-striped": striped})} {...props} />;
}

function EmptyTable({text}) {
  return text ? <p>{text}</p> : null;
}

function Row({item, columns, rowProps}) {
  const props = whenFunction(f => f(item))(rowProps);
  return <tr {...props} >
    {columns.map((column, i) => <Cell column={column} row={item} key={i} />)}
  </tr>;
}

function ColumnHeader({column}) {
  const props = R.pipe(
    propOrEmpty("headerProps"),
    whenFunction(R.applyTo(column))
  )(column);
  return <th className={styles.header} {...props}>{column.header}</th>;
}

function Cell({column, row}) {
  const renderer = toAccessor(column.accessor);
  const props = R.pipe(
    propOrEmpty("cellProps"),
    whenFunction(f => f(row, column))
  )(column);
  return <td {...props}>{renderer(row)}</td>;
}

const propOrEmpty = R.propOr({});
const toAccessor = R.cond([
  [R.is(String), R.prop],
  [R.is(Array), R.path],
  [R.is(Function), R.identity]
]);
//Used to transform functions into props
//whenFunction(tranformer)(someFunction) == transformer(someFunction)
//whenFunction(tranformer)(someObject) == someObject
const whenFunction = R.when(R.is(Function));



// WEBPACK FOOTER //
// ./modules/issFrontend/widgets/bootstrap/Table.jsx